import { useEffect, useRef } from "react";
import type { OrchestrationThreadActivity } from "@cleo/contracts";
import { useNeucleosWorldStore } from "../../neucleos/worldStore";
import {
  validLiveAgentReference,
  type LiveAgentReference,
  type LiveScope,
  type LiveState,
} from "./liveTalkController";

export interface LivePresentationRead {
  ok: true;
  worldKey: string;
  actorId: string;
  conversation: LiveScope["conversation"];
  presentation: {
    requestKey: string;
    createdAt: number;
    expiresAt: number;
    agent?: LiveAgentReference;
  } | null;
}
type ReadPresentation = (
  body: Record<string, unknown>,
  signal: AbortSignal,
) => Promise<LivePresentationRead>;
const object = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
export function liveVoicePresentationCompletion(activity: OrchestrationThreadActivity): boolean {
  if (activity.kind !== "tool.completed") return false;
  const data = object(object(activity.payload)?.data);
  const name = object(data?.item)?.tool ?? data?.toolName;
  return (
    typeof name === "string" &&
    (name === "neucleos_open_live_voice" || name.endsWith("__neucleos_open_live_voice"))
  );
}
const idle = (phase: LiveState["phase"]) =>
  phase === "off" || phase === "ended" || phase === "error";
// Presentation identities remain consumed across composer remounts, only through their short lifetime.
const consumed = new Map<string, number>();

/** A fresh tool completion only invalidates this addressed read; tool prose never opens audio. */
export function useLiveVoicePresentation(
  scope: LiveScope | null,
  activities: ReadonlyArray<OrchestrationThreadActivity> | undefined,
  phase: LiveState["phase"],
  quoteCall: (agent?: LiveAgentReference) => Promise<void>,
  readPresentation: ReadPresentation,
) {
  const revision = useNeucleosWorldStore((state) => state.grantRevision);
  const completion = activities?.findLast(liveVoicePresentationCompletion)?.id;
  const scopeKey = JSON.stringify(scope);
  const boundary = JSON.stringify([scopeKey, revision]);
  const latest = useRef({ boundary, phase });
  latest.current = { boundary, phase };
  const observed = useRef({
    boundary: "",
    initialized: false,
    since: Date.now(),
    ids: new Set<string>(),
  });
  useEffect(() => {
    const cursor = observed.current;
    // The initial activity snapshot is history, including on a new scope or grant.
    if (cursor.boundary !== boundary || !cursor.initialized) {
      observed.current = {
        boundary,
        initialized: activities !== undefined,
        since: Date.now(),
        ids: new Set(activities?.map((activity) => activity.id) ?? []),
      };
      return;
    }
    if (!activities) return;
    const fresh = activities.filter(
      (activity) =>
        !cursor.ids.has(activity.id) &&
        liveVoicePresentationCompletion(activity) &&
        Date.parse(activity.createdAt) >= cursor.since,
    );
    for (const activity of activities) cursor.ids.add(activity.id);
    if (!scope || !fresh.length || !idle(phase)) return;
    const stillCurrent = () => {
      const world = useNeucleosWorldStore.getState();
      return (
        latest.current.boundary === boundary &&
        idle(latest.current.phase) &&
        world.signedIn &&
        world.activeWorldKey === scope.worldKey &&
        world.grantRevision === revision &&
        world.worlds.find((entry) => entry.worldKey === scope.worldKey)?.actorId ===
          scope.expectedActorId
      );
    };
    if (!stillCurrent()) return;
    const abort = new AbortController();
    const request = {
      worldKey: scope.worldKey,
      expectedActorId: scope.expectedActorId,
      conversation: scope.conversation,
    };
    void readPresentation(request, abort.signal)
      .then((result) => {
        if (
          abort.signal.aborted ||
          !stillCurrent() ||
          result.ok !== true ||
          result.worldKey !== scope.worldKey ||
          result.actorId !== scope.expectedActorId ||
          !Object.entries(scope.conversation).every(
            ([key, value]) =>
              result.conversation?.[key as keyof LiveScope["conversation"]] === value,
          )
        )
          return;
        const intent = result.presentation;
        const now = Date.now();
        if (
          !intent ||
          (intent.agent !== undefined && !validLiveAgentReference(intent.agent)) ||
          typeof intent.requestKey !== "string" ||
          !intent.requestKey ||
          !Number.isSafeInteger(intent.createdAt) ||
          !Number.isSafeInteger(intent.expiresAt) ||
          intent.createdAt > now ||
          intent.createdAt < now - 120_000 ||
          intent.expiresAt <= now ||
          intent.expiresAt <= intent.createdAt ||
          intent.expiresAt - intent.createdAt > 120_000
        )
          return;
        for (const [key, expiresAt] of consumed) if (expiresAt <= now) consumed.delete(key);
        const key = JSON.stringify([
          scope.worldKey,
          scope.expectedActorId,
          scope.conversation,
          intent.requestKey,
        ]);
        if (consumed.has(key)) return;
        consumed.set(key, intent.expiresAt);
        // quoteCall still owns current scope/generation checks; it cannot start media.
        return intent.agent ? quoteCall(intent.agent) : quoteCall();
      })
      .catch(() => {
        /* A refused or interrupted addressed read never opens consent. */
      });
    return () => abort.abort();
  }, [completion, activities === undefined, boundary, phase, quoteCall, readPresentation]);
}
